import { useEffect, useMemo, useState } from 'react';
import Modal from './Modal';
import { creditsApi, formatCurrency } from '../utils/api';
import { toast } from './Toast';
import { formatDateShort } from '../utils/dateFormat';
import { useSettings } from '../context/SettingsContext';
import { track, EVENTS } from '../utils/analytics';
import { creditDocNetBasis, creditMatch } from '../utils/creditMatch';
import { round2, vatMultiplier } from '../utils/money';
import Icon from './Icon';
import EmptyState from './EmptyState';
import { useConfirm } from '../utils/useConfirm';

// Post-save modal: presented after a חשבונית זיכוי / תעודת החזרה is saved.
// Lists the supplier's open credits (status='פתוח') and lets the user pick
// which ones this credit/return document closes. Confirming marks each
// selected credit as 'התקבל', restores the underlying item's credit_qty/
// total to zero, recomputes the source document's totals, and flips the
// source from 'לא תקין' back to 'תקין' once no open credits remain.
//
// Mirrors LinkDeliveryNotesModal in look and behavior — a side-by-side
// running sum vs the credit doc's total, with an explicit confirm when the
// numbers don't match.
export default function LinkOpenCreditsModal({
  creditDocId,         // id of the saved חשבונית זיכוי / תעודת החזרה
  creditDocTotal,      // its gross total (subtotal + vat) — shown for info
  creditDocSubtotal,   // its NET total — the basis we actually compare against
  creditDocVat,        // its VAT — used to recover net when subtotal is missing
  supplierId,
  supplierName,
  onClose,
  onSaved,
}) {
  const { vatRate } = useSettings();
  const [loading, setLoading]   = useState(true);
  const [askConfirm, confirmDialog] = useConfirm();
  const [saving,  setSaving]    = useState(false);
  const [rows,    setRows]      = useState([]);
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    creditsApi.getOpenList(supplierId)
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast('שגיאה בטעינת זיכויים פתוחים', 'error'))
      .finally(() => setLoading(false));
  }, [supplierId]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const vatMult = vatMultiplier(vatRate);

  // Open credits are stored NET. selectedSum is therefore net; its gross
  // counterpart is shown alongside for readability.
  const selectedSum = useMemo(() => {
    let sum = 0;
    for (const r of rows) if (selected.has(r.id)) sum += (parseFloat(r.credit_total) || 0);
    return round2(sum);
  }, [rows, selected]);
  const selectedSumGross = round2(selectedSum * vatMult);

  // Compare NET-to-NET: the doc's net basis (subtotal, or total stripped of
  // VAT for older docs) vs the selected open credits' net sum. Comparing the
  // net sum against the gross doc total was the long-standing bug that made
  // every exact match look off by the VAT amount.
  // Credit docs are stored negative — show the gross as a positive magnitude.
  const docTotal = round2(Math.abs(parseFloat(creditDocTotal) || 0)); // gross, info only
  const docNet = creditDocNetBasis(
    { subtotal: creditDocSubtotal, total: creditDocTotal, vat: creditDocVat },
    vatRate,
  );
  const { diff, matches } = creditMatch(selectedSum, docNet);

  const handleSave = () => {
    if (selected.size === 0) {
      toast('לא נבחרו זיכויים', 'error');
      return;
    }
    if (!matches) {
      const fmtDiff = (diff > 0 ? '+' : '') + formatCurrency(diff);
      askConfirm({
        title: 'הסכומים אינם תואמים',
        message: 'לקשר את הזיכויים בכל זאת?',
        detail: `זיכויים שנבחרו ללא מע״מ: ${formatCurrency(selectedSum)} · מסמך הזיכוי ללא מע״מ: ${formatCurrency(docNet)} · הפרש: ${fmtDiff}`,
        confirmLabel: 'קשר בכל זאת',
        onConfirm: doSave,
      });
      return;
    }
    doSave();
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const ids = [...selected];
      await creditsApi.linkBulk(creditDocId, ids);
      track(EVENTS.OPEN_CREDITS_LINKED, { linked_count: ids.length, matched_totals: matches });
      toast(`קושרו ונסגרו ${ids.length} זיכויים`);
      onSaved?.(ids);
      onClose();
    } catch (e) {
      toast(e.response?.data?.error || 'שגיאה בקישור הזיכויים', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      closeOnBackdrop={false}
      ariaLabel="קישור זיכויים פתוחים"
    >
      <h2 className="modal-title" style={{ marginBottom: 6 }}>קישור זיכויים פתוחים</h2>
      <div style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', marginBottom: 16 }}>
        {supplierName} — סכום מסמך הזיכוי:{' '}
        <span style={{ fontWeight: 700, color: 'var(--ink)' }}>{formatCurrency(docNet)}</span>
        <span style={{ color: 'var(--ink-muted)' }}> ללא מע״מ · {formatCurrency(docTotal)} כולל מע״מ</span>
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
      ) : rows.length === 0 ? (
        <EmptyState message="אין זיכויים פתוחים עבור ספק זה" />
      ) : (
        <>
          <div className="table-wrapper" style={{ marginBottom: 12 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={selected.size === rows.length}
                      onChange={toggleAll}
                      title="בחר הכל"
                    />
                  </th>
                  <th>תאריך</th>
                  <th>מסמך מקור</th>
                  <th>תיאור פריט</th>
                  <th>מק"ט</th>
                  <th>כמות</th>
                  <th>סך לזיכוי</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const checked = selected.has(r.id);
                  return (
                    <tr key={r.id} onClick={() => toggle(r.id)}
                        style={{ cursor: 'pointer', background: checked ? 'var(--selected-soft-bg)' : 'var(--warning-bg)' }}>
                      <td><input type="checkbox" checked={checked} onChange={() => toggle(r.id)} onClick={e => e.stopPropagation()} /></td>
                      <td>{formatDateShort(r.doc_date) || '—'}</td>
                      <td style={{ color: 'var(--ink-muted)' }}>{r.doc_number || '—'}</td>
                      <td>{r.item_description}</td>
                      <td style={{ color: 'var(--ink-muted)', fontSize: '0.85rem' }}>{r.item_sku || '—'}</td>
                      <td>
                        {(r.credit_qty || 0) > 0 ? r.credit_qty : '—'}
                        {(r.credit_price_diff || 0) > 0 && (
                          <div style={{ fontSize: '0.72rem', color: 'var(--warning-ink)', marginTop: 2 }}>
                            הפרש מחיר: {formatCurrency(r.credit_price_diff)}
                          </div>
                        )}
                      </td>
                      <td style={{ fontWeight: 700, color: 'var(--warning-ink)' }}>{formatCurrency(r.credit_total)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--tint-strong)', fontWeight: 700 }}>
                  <td colSpan={6} style={{ padding: '10px 16px' }}>
                    נבחרו {selected.size} מתוך {rows.length} זיכויים
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--warning-ink)' }}>
                  {formatCurrency(selectedSum)}
                  <div style={{ fontSize: '0.72rem', color: 'var(--warning-ink)', fontWeight: 600 }}>
                    {formatCurrency(selectedSumGross)} כולל מע״מ
                  </div>
                </td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '10px 14px', borderRadius: 8, marginBottom: 12,
                        background: matches ? 'var(--success-bg)' : 'var(--warning-bg)',
                        border: `1px solid ${matches ? 'var(--success-line)' : 'var(--warning-line)'}` }}>
            <div style={{ fontSize: '0.9rem', color: matches ? 'var(--success-ink)' : 'var(--warning-ink)' }}>
              <Icon name={matches ? 'check-circle' : 'alert'} size={15} />{' '}
              {matches
                ? 'סכום הזיכויים שנבחרו (ללא מע״מ) שווה לסכום מסמך הזיכוי'
                : `הפרש (ללא מע״מ): ${diff > 0 ? '+' : ''}${formatCurrency(diff)}  (סכום הנבחר ${diff > 0 ? 'גבוה' : 'נמוך'} ב-${formatCurrency(Math.abs(diff))})`}
            </div>
          </div>
        </>
      )}

      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
          דלג
        </button>
        <button className="btn btn-success" onClick={handleSave} disabled={loading || saving || rows.length === 0}>
          {saving
            ? 'שומר…'
            : <><Icon name={matches ? 'check' : 'alert'} size={16} /> {matches ? 'קשר וסגור זיכויים' : 'קשר בכל זאת'}</>}
        </button>
      </div>

      {/* Inside the stopPropagation box — see ClassificationManager for why an
          un-stopped click on ConfirmModal's own backdrop otherwise bubbles
          through to this modal's onClose and closes both at once. */}
      {confirmDialog}
    </Modal>
  );
}
